/**
 * BuildingEditorMap - Map component for building editor
 *
 * Based on MapboxScene but:
 * - No aircraft loading/display
 * - Adds "Add" dropdown menu (Hangar, Ramp Area, Tiedown Area)
 * - Supports selection and hover states for buildings
 * - Shows preview building when in add mode
 * - Supports drag/rotate for building placement
 */

import { useEffect, useEffectEvent, useRef, useState, useMemo, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import * as THREE from 'three'
import { Plus, ChevronDown, Building2, ParkingSquare, Anchor, Box, LayoutGrid, Eye, EyeOff, Lock, Unlock, Waypoints, Tag, Type } from 'lucide-react'
import { useMapStore, getMapStyleUrl, isStandardStyle, DEFAULT_MAPBOX_CONFIG } from '@/stores/mapStore'
import { hideClassicStyleLabels } from '@/lib/utils/map-labels'
import { MapStyleSelector } from '@/components/map/MapStyleSelector'
import { useBuildingEditorStore } from '@/stores/buildingEditorStore'
import { useUserPreferences } from '@/contexts/UserPreferencesContext'
import {
  MapboxCustomLayerService,
  type Building3DConfig,
  SceneBuilder,
  createPreviewFromStoreData,
  createSingleRowPreview,
  createDoubleRowPreview,
  createTHangarPreview,
  createNestedTHangarPreview,
  createArchHangarPreview,
  applyPreviewStyling,
} from '@/services/3d'
import { renderBuildingFromConfig } from '@/services/3d/rendering/renderBuildingFromConfig'
import { renderOutdoorRectangularBuilding } from '@/services/3d/rendering/orchestrators/OutdoorRectangularOrchestrator'
import { useMapBuildings, useBuildingDragInteraction } from '@/hooks'
import { useObstacleRendering } from '@/hooks/useObstacleRendering'
import { useObstacleDragInteraction } from '@/hooks/useObstacleDragInteraction'
import { useSubunitRendering } from '@/hooks/useSubunitRendering'
import { useSubunitDragInteraction } from '@/hooks/useSubunitDragInteraction'
import { useSubunitStore } from '@/stores/subunitStore'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useObstacleStore } from '@/stores/obstacleStore'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { ObstaclePlacementPreview } from './obstacles/ObstaclePlacementPreview'
import { ObstacleLabelsOverlay } from './obstacles/ObstacleLabelsOverlay'
import { SubunitPlacementPreview } from './subunits/SubunitPlacementPreview'
import { SubunitLabelsOverlay } from './subunits/SubunitLabelsOverlay'
import { useTaxiPlanLoader } from '@/demo/masters-planner/hooks/useTaxiPlanLoader'
import { useTaxiEditorInteraction } from '@/demo/masters-planner/hooks/useTaxiEditorInteraction'
import { TaxiEditorOverlay } from '@/demo/masters-planner/components/TaxiEditorOverlay'
import { TaxiEditorPanel } from '@/demo/masters-planner/components/TaxiEditorPanel'
import { useTaxiEditorStore } from '@/demo/masters-planner/store/taxiEditorStore'

import { BuildingControls } from './BuildingControls'
import { BuildingLabelsOverlay } from './BuildingLabelsOverlay'
import { getObstacleScenePosition } from '@/lib/utils/obstacle-coordinates'
import { DragDebugOverlay } from '@/components/debug/DragDebugOverlay'
import { FEET_TO_METERS } from '@/constants/units'
import 'mapbox-gl/dist/mapbox-gl.css'

// Mapbox access token from environment
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN


interface BuildingEditorMapProps {
  className?: string
  locationId?: string
  onDuplicateClick?: (buildingId: string) => void
}

// Scene objects interface for interaction hook
interface SceneObjectsState {
  scene: THREE.Scene | null
  camera: THREE.Camera | null
  buildings: THREE.Group[]
  groundPlane: THREE.Mesh | null
  rotationRing: THREE.Group | null
}

export function BuildingEditorMap({ className = '', locationId, onDuplicateClick }: BuildingEditorMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const layerServiceRef = useRef<MapboxCustomLayerService | null>(null)
  const previewGroupRef = useRef<THREE.Group | null>(null)
  const rotationRingRef = useRef<THREE.Group | null>(null)
  const obstacleRotationRingRef = useRef<THREE.Group | null>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [is3DLayerAdded, setIs3DLayerAdded] = useState(false)
  const [buildingSyncTrigger, setBuildingSyncTrigger] = useState(0)
  const [mapBearing, setMapBearing] = useState(0)
  const [sceneObjectsState, setSceneObjectsState] = useState<SceneObjectsState>({
    scene: null,
    camera: null,
    buildings: [],
    groundPlane: null,
    rotationRing: null,
  })
  // Track which locationId the current 3D layer was built for
  const activeLocationIdRef = useRef<string | undefined>(undefined)
  // Tracks the last camera position set by map init or jumpTo — prevents redundant flyTo
  const settledViewSettingsRef = useRef<string | null>(null)

  // Fetch buildings from database
  const {
    buildings: dbBuildings,
    viewSettings: dbViewSettings,
    isLoading: buildingsLoading,
  } = useMapBuildings(locationId)

  // Map store
  const {
    viewSettings: storeViewSettings,
    mapStyleId,
    setLoading,
  } = useMapStore()

  // Building editor store
  const {
    mode,
    templateType,
    previewBuilding,
    singleRowFormValues,
    doubleRowFormValues,
    tHangarFormValues,
    nestedTHangarFormValues,
    archHangarFormValues,
    outdoorFormValues,
            editingBuildingId,
    editingUnitId,
    isPlaced,
    mapCenter,
    pendingOutdoorSubtype,
        enterAddMode,
    enterOutdoorAddMode,
        startEditBuilding,
                    setMapCenter,
  } = useBuildingEditorStore()

  // User preferences (for debug mode)
  const { debugMode } = useUserPreferences()

  // Obstacle store (for placement preview + focus + selection ring + visibility)
  const isPlacingObstacle = useObstacleStore((s) => s.isPlacingMode)
  const isObstacleCreatingMode = useObstacleStore((s) => s.isCreatingMode)
  const focusObstacleId = useObstacleStore((s) => s.focusObstacleId)
  const requestFocusOnObstacle = useObstacleStore((s) => s.requestFocusOnObstacle)
  const selectedObstacleId = useObstacleStore((s) => s.selectedObstacleId)
  const obstaclesVisible = useObstacleStore((s) => s.obstaclesVisible)
  const toggleObstaclesVisible = useObstacleStore((s) => s.toggleObstaclesVisible)

  // Subunit store (for view-mode creating + visibility)
  const isSubunitCreatingMode = useSubunitStore((s) => s.isCreatingMode)
  const subunitsVisible = useSubunitStore((s) => s.subunitsVisible)
  const toggleSubunitsVisible = useSubunitStore((s) => s.toggleSubunitsVisible)
  const constrainToParent = useSubunitStore((s) => s.constrainToParent)
  const toggleConstrainToParent = useSubunitStore((s) => s.toggleConstrainToParent)

  // Building label display mode: 'code' (area code, default) or 'name' (full name)
  const [labelMode, setLabelMode] = useState<'code' | 'name'>('code')

  // ── Taxi waypoint editor (portable module, shared with the event planner) ──
  const [taxiEditing, setTaxiEditing] = useState(false)
  const taxiCenter = useMemo<[number, number] | null>(
    () => (mapCenter ? [mapCenter.lng, mapCenter.lat] : null),
    [mapCenter]
  )
  const { plans: taxiPlans, activePlanId, setActivePlanId } = useTaxiPlanLoader({
    locationId,
    mapCenter: mapCenter ?? undefined,
    fieldGraph: undefined, // designer authors from scratch (seed empty)
  })
  const taxiDraft = useTaxiEditorStore((s) => s.draftPlan)
  const taxiSelNode = useTaxiEditorStore((s) => s.selectedNodeId)
  const taxiSelEdge = useTaxiEditorStore((s) => s.selectedEdgeId)
  const taxiAnchor = useTaxiEditorStore((s) => s.edgeAnchorNodeId)
  // Activation dimming reference time (designer has no event clock).
  const designerNow = useMemo(() => Date.now(), [])
  useTaxiEditorInteraction({
    map: mapRef.current,
    center: taxiCenter,
    enabled: taxiEditing && isMapLoaded,
  })

  // Setup drag/rotate interaction (suspended while editing taxiways).
  useBuildingDragInteraction({
    map: mapRef.current,
    sceneObjects: sceneObjectsState,
    enabled: is3DLayerAdded && isMapLoaded && !taxiEditing,
  })

  // Wire obstacle rendering — fetches obstacles, pushes to 3D layer
  // is3DLayerAdded ensures re-render when layerServiceRef gets set
  const { obstacles: locationObstacles } = useObstacleRendering({
    locationId,
    buildings: dbBuildings ?? [],
    layerService: is3DLayerAdded ? layerServiceRef.current : null,
    syncTrigger: buildingSyncTrigger,
  })

  // Wire obstacle drag interaction — view mode (creating/selecting)
  useObstacleDragInteraction({
    map: mapRef.current,
    sceneObjects: sceneObjectsState,
    enabled: is3DLayerAdded && isMapLoaded && mode === 'view' && obstaclesVisible && !taxiEditing,
    obstacles: locationObstacles,
    locationId: locationId ?? '',
    buildings: dbBuildings ?? [],
    editingBuildingId,
    mapCenter,
    onObstacleUpdate: () => {
      mapRef.current?.triggerRepaint()
    },
  })

  // Wire subunit rendering — fetches subunits, pushes to 3D layer
  const { subunits: locationSubunits } = useSubunitRendering({
    locationId,
    buildings: dbBuildings ?? [],
    layerService: is3DLayerAdded ? layerServiceRef.current : null,
    syncTrigger: buildingSyncTrigger,
  })

  // Wire subunit drag interaction — view mode (creating/selecting)
  useSubunitDragInteraction({
    map: mapRef.current,
    sceneObjects: sceneObjectsState,
    enabled: is3DLayerAdded && isMapLoaded && mode === 'view' && subunitsVisible && !taxiEditing,
    subunits: locationSubunits,
    locationId: locationId ?? '',
    buildings: dbBuildings ?? [],
    editingBuildingId,
    editingUnitId: editingUnitId ?? null,
    mapCenter,
    onSubunitUpdate: () => {
      mapRef.current?.triggerRepaint()
    },
  })

  // Sync subunit 3D visibility to layer service
  useEffect(() => {
    if (!layerServiceRef.current || !is3DLayerAdded) return
    layerServiceRef.current.setSubunitsVisible(subunitsVisible)
    mapRef.current?.triggerRepaint()
  }, [subunitsVisible, is3DLayerAdded])

  // Clear subunit selection/placing when entering edit mode (subunit sidebar handles view-mode)
  useEffect(() => {
    if (mode === 'edit') {
      useSubunitStore.getState().clearSelection()
      useSubunitStore.getState().cancelPlacing()
      useSubunitStore.getState().cancelCreating()
    }
  }, [mode])

  // View settings: DB data when locationId is provided, store defaults otherwise
  // Returns undefined while location data is loading — viewSettingsReady guards prevent premature use
  const viewSettings = useMemo(() => {
    if (locationId) return dbViewSettings // undefined while loading
    return storeViewSettings // Only used when no locationId (standalone mode)
  }, [locationId, dbViewSettings, storeViewSettings])

  // Whether view settings from the database are ready (defers map init until location data loads)
  const viewSettingsReady = !locationId || !!dbViewSettings

  // Filter out building being edited (it will be shown as preview instead)
  const buildingsToRender = useMemo(() => {
    let buildings = dbBuildings ?? []

    // Filter out building being edited (shown as preview instead)
    if (editingBuildingId) {
      buildings = buildings.filter(b => b.id !== editingBuildingId)
    }

    return buildings
  }, [dbBuildings, editingBuildingId])

  // Convert preview building to Building3DConfig
  // Uses worldX/worldZ directly - no GPS-to-world conversion needed (already in feet)
  const previewBuildingConfig = useMemo((): Building3DConfig | null => {
    if (!previewBuilding) return null

    return {
      id: 'preview-building',
      name: previewBuilding.name,
      width: previewBuilding.width_ft,
      depth: previewBuilding.depth_ft,
      height: previewBuilding.height_ft,
      wallThickness: 2,
      rotation: previewBuilding.bearing_deg,
      // Use world coordinates directly - no conversion needed
      // Y position is elevation from previewBuilding
      position: [previewBuilding.worldX, previewBuilding.elevation_ft, previewBuilding.worldZ],
      floorColor: previewBuilding.floor_color,
      wallColor: previewBuilding.wall_color,
    }
  }, [previewBuilding])

  // Handle "Add Building" button click - just enter add mode (template selector will show)
  const handleAddBuilding = useCallback(() => {
    enterAddMode()
  }, [enterAddMode])

  // Handle "Add Outdoor Area" — enter outdoor add mode (template selector will show)
  const handleAddOutdoorArea = useCallback((subtype: 'ramp_area' | 'tiedown_area') => {
    enterOutdoorAddMode(subtype)
  }, [enterOutdoorAddMode])

  // useEffectEvent: read latest viewSettings without adding to effect deps (React 19.2)
  const getViewSettings = useEffectEvent(() => viewSettings)

  // Initialize map — deferred until viewSettings are ready (location data loaded)
  useEffect(() => {
    // Guard: Prevent re-initialization if map already exists
    if (mapRef.current) return

    // Wait for location view settings before creating map (prevents Detroit flash)
    if (!viewSettingsReady) return

    if (!mapContainerRef.current || !MAPBOX_TOKEN) {
      console.error('BuildingEditorMap: Missing container or token')
      return
    }

    setLoading(true)

    mapboxgl.accessToken = MAPBOX_TOKEN

    // Read latest view settings via useEffectEvent (no stale closure)
    const vs = getViewSettings()
    if (!vs) return // Should not happen (viewSettingsReady guards), but type-safe

    const mapStyle = getMapStyleUrl(mapStyleId)

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center: vs.center,
      zoom: vs.zoom,
      pitch: vs.pitch,
      bearing: vs.bearing,
      antialias: true,
      keyboard: false,
      attributionControl: false,
    })

    map.addControl(new mapboxgl.AttributionControl({ compact: true }))

    mapRef.current = map

    map.on('load', () => {
      console.log('BuildingEditorMap: Map loaded at', vs.center)
      setIsMapLoaded(true)
      setLoading(false)
      // Set initial map center in store
      setMapCenter({ lat: vs.center[1], lng: vs.center[0] })
      // Record as "settled" — prevents redundant flyTo
      settledViewSettingsRef.current = JSON.stringify(vs)
      // Set initial bearing for arrow controls
      setMapBearing(map.getBearing())

      // CRITICAL WORKAROUND: Re-order event listeners by calling disable/enable
      // This fixes issue where dragPan.disable() doesn't work during mousedown
      // See: https://github.com/mapbox/mapbox-gl-js/issues/2419
      map.dragPan.disable()
      map.dragPan.enable()
      map.dragRotate.disable()
      map.dragRotate.enable()
      console.log('BuildingEditorMap: Reordered dragPan/dragRotate listeners for proper disable support')
    })

    // Hide labels on classic styles after style loads
    map.on('style.load', () => {
      const currentStyleId = useMapStore.getState().mapStyleId
      if (!isStandardStyle(currentStyleId)) {
        hideClassicStyleLabels(map, DEFAULT_MAPBOX_CONFIG)
      }
    })

    // Update bearing in real-time during rotation (for responsive arrow controls)
    map.on('rotate', () => {
      setMapBearing(map.getBearing())
    })

    // Log camera state when map stops moving
    // NOTE: mapCenter is only set once on 'load' and should NOT be updated here
    // because it's used as the fixed model origin for coordinate conversions
    map.on('moveend', () => {
      const center = map.getCenter()
      const zoom = map.getZoom()
      const pitch = map.getPitch()
      const bearing = map.getBearing()
      setMapBearing(bearing)
      console.log(
        `Camera: center=[${center.lng.toFixed(6)}, ${center.lat.toFixed(6)}], ` +
          `zoom=${zoom.toFixed(2)}, pitch=${pitch.toFixed(1)}, bearing=${bearing.toFixed(1)}`
      )
    })

    // WebGL context loss/restore handling
    // Context loss can happen during location switches or GPU pressure.
    // preventDefault() tells the browser to attempt automatic restore.
    const canvas = map.getCanvas()
    const handleContextLost = (e: Event) => {
      console.warn('BuildingEditorMap: WebGL context lost — will attempt restore')
      e.preventDefault()
    }
    const handleContextRestored = () => {
      console.log('BuildingEditorMap: WebGL context restored — rebuilding 3D layer')
      // Remove stale layer before disposing
      if (map.getLayer('three-js-layer')) {
        map.removeLayer('three-js-layer')
      }
      try {
        layerServiceRef.current?.dispose()
      } catch (e) {
        console.warn('BuildingEditorMap: dispose error during context restore', e)
      }
      layerServiceRef.current = null
      previewGroupRef.current = null
      rotationRingRef.current = null
      obstacleRotationRingRef.current = null
      setIs3DLayerAdded(false)
      setSceneObjectsState({ scene: null, camera: null, buildings: [], groundPlane: null, rotationRing: null })
    }
    canvas.addEventListener('webglcontextlost', handleContextLost)
    canvas.addEventListener('webglcontextrestored', handleContextRestored)

    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost)
      canvas.removeEventListener('webglcontextrestored', handleContextRestored)

      // Remove custom layer FIRST (stops Mapbox render callbacks before we tear down Three.js)
      if (map.getLayer('three-js-layer')) {
        map.removeLayer('three-js-layer')
      }

      // Dispose Three.js renderer before destroying the map's WebGL context
      try {
        layerServiceRef.current?.dispose()
      } catch (e) {
        console.warn('BuildingEditorMap: dispose error during cleanup', e)
      }

      map.remove()
      mapRef.current = null
      layerServiceRef.current = null
      previewGroupRef.current = null
      rotationRingRef.current = null
      obstacleRotationRingRef.current = null
      setIs3DLayerAdded(false)
      setSceneObjectsState({
        scene: null,
        camera: null,
        buildings: [],
        groundPlane: null,
        rotationRing: null,
      })
    }
  }, [viewSettingsReady]) // Only re-check when readiness changes

  // Resize the Mapbox canvas when the container dimensions change
  // (e.g. the editing side panel opens/closes and the flex-1 map column resizes).
  useEffect(() => {
    const map = mapRef.current
    const container = mapContainerRef.current
    if (!map || !container || !isMapLoaded) return

    const ro = new ResizeObserver(() => {
      map.resize()
    })
    ro.observe(container)

    return () => ro.disconnect()
  }, [isMapLoaded])

  // Tear down and recreate Three.js layer when location changes
  // Uses jumpTo (instant) instead of flyTo for cross-location switches
  useEffect(() => {
    // First mount — just record the initial locationId
    if (activeLocationIdRef.current === undefined) {
      activeLocationIdRef.current = locationId
      return
    }
    // Same location — nothing to do
    if (locationId === activeLocationIdRef.current) return

    console.log(`BuildingEditorMap: Location changed ${activeLocationIdRef.current} → ${locationId}`)
    activeLocationIdRef.current = locationId

    // Reset building editor store
    useBuildingEditorStore.getState().reset()

    const map = mapRef.current
    const svc = layerServiceRef.current

    if (map && svc && is3DLayerAdded) {
      // Remove custom layer from Mapbox (must happen before dispose so renderer can resetState)
      if (map.getLayer('three-js-layer')) {
        map.removeLayer('three-js-layer')
      }

      // Dispose renderer + scene graph (prevents WebGL context corruption on recreate)
      svc.dispose()
      layerServiceRef.current = null
      previewGroupRef.current = null
      rotationRingRef.current = null
      obstacleRotationRingRef.current = null

      // Trigger layer recreation
      setIs3DLayerAdded(false)
      setSceneObjectsState({
        scene: null,
        camera: null,
        buildings: [],
        groundPlane: null,
        rotationRing: null,
      })
    }

    // Jump camera to new location (instant, no animation)
    if (map && isMapLoaded) {
      const vs = getViewSettings()
      if (vs) {
        setMapCenter({ lat: vs.center[1], lng: vs.center[0] })
        map.jumpTo({
          center: vs.center,
          zoom: vs.zoom,
          pitch: vs.pitch,
          bearing: vs.bearing,
        })
        settledViewSettingsRef.current = JSON.stringify(vs)
      }
    }
  }, [locationId, is3DLayerAdded, isMapLoaded])

  // Add Three.js layer after map is loaded
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded || is3DLayerAdded) return

    // Read view settings — must be available for correct transform matrix
    const vs = getViewSettings()
    if (!vs) return // Location data not ready yet

    const layerService = new MapboxCustomLayerService()
    layerServiceRef.current = layerService

    // Create the layer WITHOUT aircraft
    const customLayer = layerService.createThreeJsLayer({
      center: vs.center,
      altitude: -3, // Negative to lower scene to ground level (Mapbox places at ~10ft above)
      enableShadows: true,
      shadowQuality: 'high',
      buildings: buildingsToRender,
      aircraft: [], // No aircraft in editor
      onSceneReady: (sceneObjects) => {
        console.log('BuildingEditorMap: 3D Scene ready with', sceneObjects.buildings.length, 'buildings')

        // Create ground plane for raycasting
        const groundPlane = SceneBuilder.createGroundPlane()
        sceneObjects.scene.add(groundPlane)

        // Create rotation ring (hidden by default)
        const rotationRing = SceneBuilder.createRotationRing()
        rotationRing.visible = false
        rotationRing.position.set(0, -10000, 0) // Start far below ground
        sceneObjects.scene.add(rotationRing)
        rotationRingRef.current = rotationRing

        // Create obstacle rotation ring (smaller, hidden by default)
        const obstacleRing = SceneBuilder.createRotationRing(1.5, 0.15)
        obstacleRing.name = 'obstacleRotationRing'
        obstacleRing.visible = false
        obstacleRing.position.set(0, -10000, 0)
        sceneObjects.scene.add(obstacleRing)
        obstacleRotationRingRef.current = obstacleRing

        // Update state for interaction hook
        setSceneObjectsState({
          scene: sceneObjects.scene,
          camera: sceneObjects.camera,
          buildings: sceneObjects.buildings,
          groundPlane: groundPlane,
          rotationRing: rotationRing,
        })
      },
    })

    // Guard: Mapbox style must be fully loaded before addLayer (can be stale after location switch)
    const addLayerWhenReady = () => {
      if (map.getLayer('three-js-layer')) return // Already added (race guard)
      map.addLayer(customLayer)
      setIs3DLayerAdded(true)
      activeLocationIdRef.current = locationId
      settledViewSettingsRef.current = JSON.stringify(vs)
      console.log('BuildingEditorMap: Three.js layer added')
    }

    if (map.isStyleLoaded()) {
      addLayerWhenReady()
    } else {
      map.once('style.load', addLayerWhenReady)
    }
  }, [isMapLoaded, is3DLayerAdded, viewSettingsReady]) // viewSettingsReady ensures re-trigger when new location data arrives

  // Sync buildings when data changes AFTER layer is created
  // Uses template-aware rendering to ensure doors and partition walls render correctly
  useEffect(() => {
    const layerService = layerServiceRef.current
    if (!layerService || !is3DLayerAdded) return

    const sceneObjects = layerService.getSceneObjects()
    if (!sceneObjects.scene) return

    console.log('🔄 Syncing buildings with template-aware rendering:', buildingsToRender.length)

    // Clear existing buildings
    layerService.clearBuildings()

    // Re-add buildings using shared template-aware rendering
    for (const building of buildingsToRender) {
      // Use shared render function for template-aware building rendering
      const buildingGroup = renderBuildingFromConfig(building, { enableShadows: true })

      // Set userData (orchestrators already set position/rotation)
      buildingGroup.userData.buildingId = building.id
      buildingGroup.name = `building-${building.id}`

      sceneObjects.scene.add(buildingGroup)
      sceneObjects.buildings.push(buildingGroup)
    }

    // Add invisible anchor group for the editing building so obstacle renderers can attach
    if (editingBuildingId) {
      const editingBuilding = (dbBuildings ?? []).find(b => b.id === editingBuildingId)
      if (editingBuilding) {
        const anchorGroup = new THREE.Group()
        anchorGroup.name = `building-${editingBuildingId}-anchor`
        anchorGroup.userData.buildingId = editingBuildingId
        anchorGroup.userData.isAnchor = true
        anchorGroup.position.set(
          editingBuilding.position[0] * FEET_TO_METERS,
          editingBuilding.position[1] * FEET_TO_METERS,
          editingBuilding.position[2] * FEET_TO_METERS
        )
        if (editingBuilding.rotation) {
          anchorGroup.rotation.y = (editingBuilding.rotation * Math.PI) / 180
        }
        sceneObjects.scene.add(anchorGroup)
        sceneObjects.buildings.push(anchorGroup)
      }
    }

    // Signal obstacle hook to re-push (clearBuildings destroyed renderers)
    setBuildingSyncTrigger(v => v + 1)

    // Update state for interaction hook with new buildings array
    setSceneObjectsState((prev) => ({
      ...prev,
      buildings: sceneObjects.buildings,
    }))

    // Trigger repaint
    mapRef.current?.triggerRepaint()
  }, [buildingsToRender, is3DLayerAdded, editingBuildingId, dbBuildings])

  // Update or create preview building when previewBuilding changes
  useEffect(() => {
    const layerService = layerServiceRef.current
    if (!layerService || !is3DLayerAdded) return

    const sceneObjects = layerService.getSceneObjects()
    if (!sceneObjects.scene) return

    console.log('🏗️ Preview building effect running:', {
      hasConfig: !!previewBuildingConfig,
      rotation: previewBuildingConfig?.rotation,
      position: previewBuildingConfig?.position,
      templateType,
    })

    // Remove existing preview
    if (previewGroupRef.current) {
      sceneObjects.scene.remove(previewGroupRef.current)
      previewGroupRef.current = null
    }

    // Add new preview if we have one
    if (previewBuilding) {
      let previewGroup: THREE.Group

      // Use appropriate preview renderer based on template type
      if (templateType === 'single_row' && singleRowFormValues) {
        // Use single-row preview renderer (multi-unit with partition walls)
        previewGroup = createSingleRowPreview(singleRowFormValues, [
          previewBuilding.worldX,
          previewBuilding.elevation_ft,
          previewBuilding.worldZ,
        ])
        console.log('🏗️ Created single-row preview with', singleRowFormValues.unit_count, 'units')
      } else if (templateType === 'double_row' && doubleRowFormValues) {
        // Use double-row preview renderer (two rows with shared spine wall)
        previewGroup = createDoubleRowPreview(doubleRowFormValues, [
          previewBuilding.worldX,
          previewBuilding.elevation_ft,
          previewBuilding.worldZ,
        ])
        const totalUnits = doubleRowFormValues.row_a.unit_count +
          (doubleRowFormValues.layout_mode === 'symmetric'
            ? doubleRowFormValues.row_a.unit_count
            : doubleRowFormValues.row_b.unit_count)
        console.log('🏗️ Created double-row preview with', totalUnits, 'units')
      } else if (templateType === 'standard_t_hangar' && tHangarFormValues) {
        // Use T-hangar preview renderer (interlocking T-shaped units)
        previewGroup = createTHangarPreview(tHangarFormValues, [
          previewBuilding.worldX,
          previewBuilding.elevation_ft,
          previewBuilding.worldZ,
        ])
        console.log('🏗️ Created T-hangar preview with', tHangarFormValues.unit_count, 'units')
      } else if (templateType === 'nested_t_hangar' && nestedTHangarFormValues) {
        // Use nested T-hangar preview renderer (two interlocked rows)
        previewGroup = createNestedTHangarPreview(nestedTHangarFormValues, [
          previewBuilding.worldX,
          previewBuilding.elevation_ft,
          previewBuilding.worldZ,
        ])
        const totalUnits = 2 * nestedTHangarFormValues.unit_count - 1
        console.log('🏗️ Created nested T-hangar preview with', totalUnits, 'units')
      } else if (templateType === 'arch_hangar' && archHangarFormValues) {
        // Use arch hangar preview renderer (single curved unit)
        previewGroup = createArchHangarPreview(archHangarFormValues, [
          previewBuilding.worldX,
          previewBuilding.elevation_ft,
          previewBuilding.worldZ,
        ])
        console.log('🏗️ Created arch hangar preview')
      } else if (templateType === 'outdoor_rectangular' && outdoorFormValues) {
        // Use outdoor rectangular renderer (flat border + optional floor)
        const generatedBuilding = {
          name: previewBuilding.name,
          code: previewBuilding.code,
          type: outdoorFormValues.area_subtype as 'ramp_area' | 'tiedown_area',
          exterior_width: previewBuilding.width_ft,
          exterior_depth: previewBuilding.depth_ft,
          exterior_height: 0,
          latitude: previewBuilding.latitude,
          longitude: previewBuilding.longitude,
          rotation: (previewBuilding.bearing_deg * Math.PI) / 180,
          door_width: 0,
          door_height: 0,
          settings: {
            floor_color: outdoorFormValues.floor_color,
            wall_color: '#A0A0A0',
            border_color: outdoorFormValues.border_color,
            border_style: outdoorFormValues.border_style as 'solid' | 'dashed',
            border_width_ft: outdoorFormValues.border_width_ft,
            floor_opacity: outdoorFormValues.floor_opacity,
          },
          type_config: { template: 'outdoor_rectangular' },
        }
        previewGroup = renderOutdoorRectangularBuilding(
          generatedBuilding,
          [],
          {
            position: [previewBuilding.worldX, 0, previewBuilding.worldZ],
            rotationDeg: previewBuilding.bearing_deg,
          }
        )
        console.log('🏗️ Created outdoor area preview:', outdoorFormValues.area_subtype)
      } else {
        // Use single-unit preview renderer
        previewGroup = createPreviewFromStoreData(previewBuilding)
        console.log('🏗️ Created single-unit preview with rotation:', previewBuilding.bearing_deg)
      }

      previewGroup.name = 'preview-building'

      // Make preview slightly transparent
      applyPreviewStyling(previewGroup)

      sceneObjects.scene.add(previewGroup)
      previewGroupRef.current = previewGroup

      // Trigger map repaint
      mapRef.current?.triggerRepaint()
    }
  }, [previewBuilding, singleRowFormValues, doubleRowFormValues, tHangarFormValues, nestedTHangarFormValues, archHangarFormValues, outdoorFormValues, templateType, is3DLayerAdded])

  // Position rotation ring at preview building
  // Only show ring when building is placed (not while following cursor)
  useEffect(() => {
    const rotationRing = rotationRingRef.current
    if (!rotationRing) return

    // Show/hide ring based on mode, preview building, and placement state
    // In add mode, only show ring after building is placed
    // In edit mode, always show ring (isPlaced defaults to true)
    const shouldShow =
      previewBuildingConfig !== null &&
      ((mode === 'add' && isPlaced) || mode === 'edit')

    if (shouldShow && previewBuildingConfig) {
      // Position ring at building center
      const xMeters = previewBuildingConfig.position[0] * FEET_TO_METERS
      const zMeters = previewBuildingConfig.position[2] * FEET_TO_METERS

      rotationRing.position.set(xMeters, 0.5, zMeters)
      rotationRing.visible = true
    } else {
      rotationRing.visible = false
      rotationRing.position.set(0, -10000, 0) // Hide below ground
    }

    // Trigger repaint
    mapRef.current?.triggerRepaint()
  }, [mode, isPlaced, previewBuildingConfig])

  // Extract only the selected obstacle (stable ref when unrelated obstacles change)
  const selectedObstacleForRing = useMemo(
    () => selectedObstacleId ? locationObstacles.find((o) => o.id === selectedObstacleId) : undefined,
    [locationObstacles, selectedObstacleId]
  )

  // Position obstacle rotation ring at selected obstacle
  useEffect(() => {
    const ring = obstacleRotationRingRef.current
    if (!ring) return

    const shouldShow =
      selectedObstacleId !== null &&
      obstaclesVisible &&
      mode === 'view'

    if (!shouldShow) {
      ring.visible = false
      ring.position.set(0, -10000, 0)
      mapRef.current?.triggerRepaint()
      return
    }

    if (!selectedObstacleForRing) {
      ring.visible = false
      ring.position.set(0, -10000, 0)
      mapRef.current?.triggerRepaint()
      return
    }

    const scenePos = getObstacleScenePosition(selectedObstacleForRing, dbBuildings ?? [])
    if (!scenePos) {
      ring.visible = false
      mapRef.current?.triggerRepaint()
      return
    }

    // Dynamic scale based on obstacle size (convert feet → meters for correct ring sizing)
    const obstacleSizeFt = Math.max(
      selectedObstacleForRing.width ?? 0,
      selectedObstacleForRing.depth ?? 0,
      (selectedObstacleForRing.radius ?? 0) * 2,
      4
    )
    const ringRadiusMeters = (obstacleSizeFt / 2 + 1) * FEET_TO_METERS
    const scaleFactor = ringRadiusMeters / 1.5 // 1.5 = base ring radius in meters
    ring.scale.set(scaleFactor, 1, scaleFactor)

    ring.position.set(
      scenePos.x * FEET_TO_METERS,
      0.3,
      scenePos.z * FEET_TO_METERS
    )
    ring.visible = true
    mapRef.current?.triggerRepaint()
  }, [mode, selectedObstacleId, obstaclesVisible, selectedObstacleForRing, dbBuildings])

  // Handle map style changes — reattach existing scene instead of full rebuild
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded) return

    const newStyle = getMapStyleUrl(mapStyleId)

    map.once('style.load', () => {
      const svc = layerServiceRef.current
      if (svc?.hasScene()) {
        // Reattach existing scene — no rebuild needed
        map.addLayer(svc.createReattachLayer())
      } else {
        // No scene yet (first load or after location change) — full rebuild
        setIs3DLayerAdded(false)
      }
    })

    map.setStyle(newStyle)
  }, [mapStyleId, isMapLoaded])

  // Handle view settings changes — uses settled ref to skip redundant flyTo
  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapLoaded || !viewSettings) return

    // Skip flyTo if camera was already positioned here (by map init or location jumpTo)
    const vsKey = JSON.stringify(viewSettings)
    if (settledViewSettingsRef.current === vsKey) return

    // Genuine settings change — animate
    settledViewSettingsRef.current = vsKey
    map.flyTo({
      center: viewSettings.center,
      zoom: viewSettings.zoom,
      pitch: viewSettings.pitch,
      bearing: viewSettings.bearing,
      duration: 1500,
    })
  }, [viewSettings, isMapLoaded])

  // Hide original building mesh when editing (prevents duplicate during edit)
  // The 3D layer effect doesn't re-run when editingBuildingId changes due to is3DLayerAdded guard,
  // so we directly hide the mesh instead of recreating the layer.
  useEffect(() => {
    if (!editingBuildingId || !sceneObjectsState.scene) return

    // Find and hide the building being edited
    const buildingToHide = sceneObjectsState.buildings.find(
      (b) => b.userData.buildingId === editingBuildingId && !b.userData.isAnchor
    )
    if (buildingToHide) {
      buildingToHide.visible = false
      mapRef.current?.triggerRepaint()
      console.log('🙈 Hiding original building during edit:', editingBuildingId)
    }

    // Cleanup: show building again when edit ends or component unmounts
    return () => {
      if (buildingToHide) {
        buildingToHide.visible = true
        mapRef.current?.triggerRepaint()
        console.log('👁️ Showing original building after edit ends:', editingBuildingId)
      }
    }
  }, [editingBuildingId, sceneObjectsState.buildings])

  // Sync obstacle 3D visibility to layer service
  useEffect(() => {
    if (!layerServiceRef.current || !is3DLayerAdded) return
    layerServiceRef.current.setObstaclesVisible(obstaclesVisible)
    mapRef.current?.triggerRepaint()
  }, [obstaclesVisible, is3DLayerAdded])

  // Clear obstacle selection on mode transitions
  useEffect(() => {
    if (mode !== 'view') {
      useObstacleStore.getState().clearSelection()
      useObstacleStore.getState().cancelCreating()
    }
  }, [mode])

  // Focus camera on obstacle when requested from panel
  useEffect(() => {
    if (!focusObstacleId || !mapRef.current || !mapCenter) return

    const obstacle = locationObstacles.find((o) => o.id === focusObstacleId)
    if (!obstacle) {
      requestFocusOnObstacle(null)
      return
    }

    const scenePos = getObstacleScenePosition(obstacle, dbBuildings ?? [])
    if (!scenePos) {
      requestFocusOnObstacle(null)
      return
    }

    // Convert scene feet to lng/lat
    const lngLat = CoordinateService.scenePositionToLngLat(
      [scenePos.x, 0, scenePos.z],
      [mapCenter.lng, mapCenter.lat]
    )

    mapRef.current.easeTo({ center: lngLat, duration: 800 })
    requestFocusOnObstacle(null)
  }, [focusObstacleId, locationObstacles, dbBuildings, mapCenter, requestFocusOnObstacle])

  return (
    <div className={`relative w-full h-full min-h-[400px] ${className}`}>
      {/* Map container */}
      <div ref={mapContainerRef} className="absolute inset-0 w-full h-full" />

      {/* Loading indicator — also shown while waiting for location view settings */}
      {(!viewSettingsReady || !isMapLoaded || buildingsLoading) && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80">
          <div className="flex flex-col items-center gap-2">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <span className="text-sm text-muted-foreground">
              {!viewSettingsReady ? 'Loading location...' : buildingsLoading ? 'Loading buildings...' : 'Loading map...'}
            </span>
          </div>
        </div>
      )}

      {/* Add Area dropdown - only in view mode */}
      {isMapLoaded && mode === 'view' && (
        <div className="absolute top-4 left-4 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Add
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  handleAddBuilding()
                }}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Hangar
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  handleAddOutdoorArea('ramp_area')
                }}
              >
                <ParkingSquare className="h-4 w-4 mr-2" />
                Ramp Area
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  handleAddOutdoorArea('tiedown_area')
                }}
              >
                <Anchor className="h-4 w-4 mr-2" />
                Tiedown Area
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  useSubunitStore.getState().cancelCreating()
                  useSubunitStore.getState().selectSubunit(null)
                  useObstacleStore.getState().startCreating(null)
                }}
              >
                <Box className="h-4 w-4 mr-2" />
                Obstacle
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  useObstacleStore.getState().cancelCreating()
                  useObstacleStore.getState().selectObstacle(null)
                  useSubunitStore.getState().startCreating(null)
                }}
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Subunit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Mode indicator - shows when in add/edit mode */}
      {isMapLoaded && mode !== 'view' && (
        <div className="absolute top-4 left-4 z-10">
          <div className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground">
            {mode === 'add'
              ? (templateType === 'outdoor_rectangular' || pendingOutdoorSubtype ? 'Adding Outdoor Area' : 'Adding Hangar')
              : mode === 'edit'
                ? (templateType === 'outdoor_rectangular' ? 'Editing Outdoor Area' : 'Editing Hangar')
                : 'Drag Mode'}
          </div>
        </div>
      )}


      {/* Map style selector + obstacle/subunit visibility toggles (hidden while editing taxiways) */}
      {isMapLoaded && !taxiEditing && (
        <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2 items-end">
          <button
            onClick={() => setLabelMode((m) => (m === 'code' ? 'name' : 'code'))}
            className="flex items-center gap-1.5 rounded-md bg-background/90 px-2.5 py-1.5 text-xs font-medium shadow-md backdrop-blur-sm transition-colors hover:bg-muted"
            title={labelMode === 'code' ? 'Showing area codes — click to show names' : 'Showing names — click to show area codes'}
          >
            {labelMode === 'code' ? <Tag className="h-3.5 w-3.5" /> : <Type className="h-3.5 w-3.5" />}
            {labelMode === 'code' ? 'Codes' : 'Names'}
          </button>
          <button
            onClick={toggleObstaclesVisible}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium shadow-md backdrop-blur-sm transition-colors ${
              obstaclesVisible ? 'bg-background/90 hover:bg-muted' : 'bg-muted/60 text-muted-foreground'
            }`}
            title={obstaclesVisible ? 'Hide obstacles' : 'Show obstacles'}
          >
            {obstaclesVisible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            Obstacles
          </button>
          <button
            onClick={toggleSubunitsVisible}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium shadow-md backdrop-blur-sm transition-colors ${
              subunitsVisible ? 'bg-background/90 hover:bg-muted' : 'bg-muted/60 text-muted-foreground'
            }`}
            title={subunitsVisible ? 'Hide subunits' : 'Show subunits'}
          >
            {subunitsVisible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            Subunits
          </button>
          <button
            onClick={() => {
              setTaxiEditing((v) => !v)
              useTaxiEditorStore.getState().setMode('view')
            }}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium shadow-md backdrop-blur-sm transition-colors ${
              taxiEditing ? 'bg-sky-500/90 text-white hover:bg-sky-500' : 'bg-muted/60 text-muted-foreground'
            }`}
            title={taxiEditing ? 'Exit taxiway editor' : 'Edit taxiways'}
          >
            <Waypoints className="h-3.5 w-3.5" />
            Taxiways
          </button>
          {subunitsVisible && mode === 'view' && (
            <button
              onClick={toggleConstrainToParent}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium shadow-md backdrop-blur-sm transition-colors ${
                constrainToParent ? 'bg-background/90 hover:bg-muted' : 'bg-muted/60 text-muted-foreground'
              }`}
              title={constrainToParent ? 'Subunit containment ON' : 'Subunit containment OFF'}
            >
              {constrainToParent ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
              Contain
            </button>
          )}
          <MapStyleSelector styles={[
            { id: 'satellite-streets', label: 'Satellite' },
            { id: 'standard', label: 'Standard' },
            { id: 'streets', label: 'Streets' },
            { id: 'light', label: 'Light' },
            { id: 'dark', label: 'Dark' },
          ]} />
        </div>
      )}

      {/* Arrow controls for precise movement */}
      {isMapLoaded && viewSettings && <BuildingControls mapBearing={mapBearing} initialBearing={viewSettings.bearing} />}

      {/* Debug overlay - shows drag state in real-time (only when debug mode enabled) */}
      {isMapLoaded && debugMode && <DragDebugOverlay map={mapRef.current} />}

      {/* Building labels - shown above all buildings in view mode */}
      {isMapLoaded && (
        <BuildingLabelsOverlay
          map={mapRef.current}
          buildings={dbBuildings ?? []}
          mapCenter={mapCenter}
          labelMode={labelMode}
          onEditClick={startEditBuilding}
          onDuplicateClick={onDuplicateClick}
          onAddObstacle={(buildingId) => {
            useSubunitStore.getState().cancelCreating()
            useSubunitStore.getState().selectSubunit(null)
            useObstacleStore.getState().startCreating(buildingId)
          }}
          onAddSubunit={(buildingId) => {
            useObstacleStore.getState().cancelCreating()
            useObstacleStore.getState().selectObstacle(null)
            useSubunitStore.getState().startCreating(buildingId)
          }}
        />
      )}

      {/* Obstacle labels - shown in view mode */}
      {isMapLoaded && obstaclesVisible && mode === 'view' && (
        <ObstacleLabelsOverlay
          map={mapRef.current}
          obstacles={locationObstacles}
          buildings={dbBuildings ?? []}
          editingBuildingId={mode === 'edit' ? editingBuildingId : null}
          mapCenter={mapCenter}
        />
      )}

      {/* Subunit labels - shown in view mode */}
      {isMapLoaded && subunitsVisible && mode === 'view' && (
        <SubunitLabelsOverlay
          map={mapRef.current}
          subunits={locationSubunits}
          buildings={dbBuildings ?? []}
          mapCenter={mapCenter}
        />
      )}

      {/* Obstacle placement preview — ghost mesh following cursor */}
      {is3DLayerAdded && (isPlacingObstacle || isObstacleCreatingMode) && (
        <ObstaclePlacementPreview
          scene={sceneObjectsState.scene}
          map={mapRef.current}
        />
      )}

      {/* Subunit placement preview — ghost mesh following cursor */}
      {is3DLayerAdded && isSubunitCreatingMode && (
        <SubunitPlacementPreview
          scene={sceneObjectsState.scene}
          map={mapRef.current}
        />
      )}

      {/* Taxi waypoint editor — overlay + authoring panel (designer mount) */}
      {isMapLoaded && taxiEditing && (
        <>
          <TaxiEditorOverlay
            map={mapRef.current}
            center={taxiCenter}
            plan={taxiDraft}
            currentTime={designerNow}
            selectedNodeId={taxiSelNode}
            selectedEdgeId={taxiSelEdge}
            edgeAnchorNodeId={taxiAnchor}
            visible={taxiEditing}
          />
          <TaxiEditorPanel
            plans={taxiPlans}
            activePlanId={activePlanId}
            onSelectPlan={setActivePlanId}
            distribution={[]}
            onClose={() => setTaxiEditing(false)}
          />
        </>
      )}

    </div>
  )
}
